import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { API_SCOPE, isAuthConfigured } from './msal.config';
import { api, type MeResponse } from '../api/client';

export interface AuthState {
  isConfigured: boolean;
  isSignedIn: boolean;
  isLoading: boolean;
  me?: MeResponse;
  error?: Error;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  hasRole: (role: string) => boolean;
  hasCapability: (capability: string) => boolean;
}

/**
 * Anmeldezustand und Berechtigungen.
 *
 * `hasRole` und `hasCapability` steuern ausschließlich die Darstellung. Die
 * eigentliche Prüfung passiert serverseitig bei jeder Aktion — eine Oberfläche,
 * die einen Knopf ausblendet, hat noch nichts abgesichert.
 */
export function useAuth(): AuthState {
  const { instance, accounts, inProgress } = useMsal();
  const isSignedIn = accounts.length > 0;

  const {
    data: me,
    isPending,
    error,
  } = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    // Ohne Anmeldung gibt es nichts abzufragen; ohne Konfiguration auch nicht.
    enabled: isAuthConfigured && isSignedIn,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const signIn = useCallback(async () => {
    await instance.loginRedirect({ scopes: [API_SCOPE] });
  }, [instance]);

  const signOut = useCallback(async () => {
    const account = instance.getActiveAccount() ?? accounts[0];
    await instance.logoutRedirect({ account });
  }, [instance, accounts]);

  return {
    isConfigured: isAuthConfigured,
    isSignedIn,
    isLoading: inProgress !== 'none' || (isSignedIn && isPending),
    me,
    error: error ?? undefined,
    signIn,
    signOut,
    hasRole: (role) => me?.roles.includes(role) ?? false,
    hasCapability: (capability) => me?.capabilities.includes(capability) ?? false,
  };
}
